import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';

// With overlaysWebView: false the StatusBar plugin puts its own opaque view
// behind the clock/battery strip, so the strip's colour is the plugin's to
// set — nothing in the WebView or the window shows through it. Left alone it
// defaults to black, which reads as a bar stuck above the app.
//
// Beware the Style names, they are the opposite of what they sound like:
// Style.Dark means "light glyphs, for a dark background" and Style.Light means
// "dark glyphs, for a light background".
const THEMES = {
  dark: { background: '#0b1220', style: Style.Dark },
  light: { background: '#ffffff', style: Style.Light },
};

const DARK_QUERY = '(prefers-color-scheme: dark)';

function applyTheme(isDark: boolean) {
  const theme = isDark ? THEMES.dark : THEMES.light;

  StatusBar.setStyle({ style: theme.style }).catch((err) => {
    console.warn('statusBar: setStyle failed', err);
  });
  StatusBar.setBackgroundColor({ color: theme.background }).catch((err) => {
    console.warn('statusBar: setBackgroundColor failed', err);
  });
}

/**
 * Keeps the native status bar in step with the device theme: white bar with
 * dark glyphs under Light Appearance, near-black bar with light glyphs under
 * Dark. No-op on web, where the browser owns the chrome.
 *
 * capacitor.config.ts carries the light values as its static defaults, which
 * is what the bar looks like before this runs.
 */
export function initStatusBar() {
  if (!Capacitor.isNativePlatform()) return;

  // capacitor.config.ts already sets this at launch; re-applying makes it
  // explicit and restores it if anything resets it later.
  StatusBar.setOverlaysWebView({ overlay: false }).catch((err) => {
    console.warn('statusBar: setOverlaysWebView failed', err);
  });

  const query = window.matchMedia(DARK_QUERY);
  applyTheme(query.matches);

  query.addEventListener('change', (event) => applyTheme(event.matches));

  // The plugin re-applies its *config* values whenever the bridge view
  // appears again — coming back from the App Store or an OAuth sheet, say —
  // which would undo the dark-mode colours. Re-assert them once we are
  // visible again.
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) applyTheme(window.matchMedia(DARK_QUERY).matches);
  });
}
